﻿import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const Login: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      navigate((location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/dashboard', { replace: true });
    }
  }, [authLoading, location.state, navigate, user]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate((location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/dashboard', { replace: true });
    } catch (loginError: any) {
      setError(loginError?.message || 'Não foi possível autenticar.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-foreground-500">
          <div className="w-5 h-5 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
          <span className="text-sm">Verificando sessão...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex">
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] bg-primary-900 relative overflow-hidden flex-col justify-between p-10">
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: 'radial-gradient(circle at 25% 25%, oklch(0.62 0.108 82 / 0.3) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          ></div>
        </div>

        <div className="relative z-10">
          <div className="w-12 h-12 rounded-xl bg-accent-500/20 border border-accent-500/30 flex items-center justify-center mb-6">
            <span className="text-accent-300 font-bold text-lg">BM</span>
          </div>
          <h1 className="text-3xl font-bold text-background-50 mb-2">BM Legal OS</h1>
          <p className="text-base text-background-50/60 max-w-sm">
            Sistema operacional jurídico do Borges Meschick Consultoria Jurídica e Advocacia
          </p>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 rounded-full bg-accent-500"></div>
            <div>
              <p className="text-sm text-background-50/80 font-medium">Painel Interno Premium</p>
              <p className="text-xs text-background-50/40">Gestão comercial, jurídica, financeira e documental</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mx-auto mb-3">
              <span className="text-background-50 font-bold text-lg">BM</span>
            </div>
            <h2 className="text-xl font-bold text-foreground-900">BM Legal OS</h2>
            <p className="text-sm text-foreground-400 mt-1">Borges Meschick</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground-900 mb-1">Acessar sistema</h2>
            <p className="text-sm text-foreground-500">Entre com suas credenciais do escritório</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>

            {error && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <div className="mt-6 p-3 bg-amber-50/50 border border-amber-100 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="ri-shield-check-line text-amber-600 text-sm mt-0.5"></i>
              <p className="text-[11px] text-amber-700 font-medium">
                Acesso restrito ao escritório Borges Meschick Consultoria Jurídica e Advocacia.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
